/**
 * Save Prices to SKU Master
 *
 * After a PO is saved, any line whose unit price differs from the SKU
 * master's current price for the PO's container type is offered here: tick
 * the ones that are real new pricing and they're recorded as confirmed quotes
 * (quotation number "PO <number>"), so future POs pre-fill with them. Danican
 * product numbers typed on lines whose item has none (or a different one) are
 * offered the same way and written onto the item.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { POLineItem } from "models/InventoryModel";
import { CatalogSuggestions } from "services/InventoryService";
import SkuMasterService from "services/SkuMasterService";
import { FreightMethod, METHOD_LABELS, METHOD_PREFERENCE, methodForContainer } from "shared/utils/freightMethods";

export type PriceChange = {
  inventoryId: string;
  name: string | undefined;
  /** Freight method the price is recorded under; null when the PO has no container type (the dialog asks) */
  method: FreightMethod | null;
  /** Method the SKU master's current price was found under (the one the editor suggested) */
  currentMethod: FreightMethod | null;
  currentPrice: number | null;
  newPrice: number;
};

/**
 * Lines whose price differs from the SKU master. With a container type the
 * comparison is against that method's price; without one it is against the
 * price the editor suggested (the first method that has one), and the dialog
 * asks which method to record the new price under.
 */
export function findPriceChanges(
  lineItems: POLineItem[],
  containerType: string | null | undefined,
  catalogSuggestions: CatalogSuggestions | null,
): PriceChange[] {
  const method = methodForContainer(containerType) ?? null;
  const changes: PriceChange[] = [];
  for (const li of lineItems) {
    if (!li.inventoryId || li.unitPrice == null) continue;
    const prices = catalogSuggestions?.prices?.[li.inventoryId] ?? {};
    const currentMethod = method ?? METHOD_PREFERENCE.find(m => prices[m] != null) ?? null;
    const current = currentMethod ? prices[currentMethod] ?? null : null;
    if (current != null && Math.abs(current - li.unitPrice) < 0.005) continue;
    changes.push({ inventoryId: li.inventoryId, name: li.inventoryName, method, currentMethod, currentPrice: current, newPrice: li.unitPrice });
  }
  return changes;
}

export type ProductNumberChange = {
  inventoryId: string;
  name: string | undefined;
  currentNumber: string | null;
  newNumber: string;
};

/** Lines carrying a Danican product number the SKU master doesn't have for that item. */
export function findProductNumberChanges(
  lineItems: POLineItem[],
  catalogSuggestions: CatalogSuggestions | null,
): ProductNumberChange[] {
  const changes: ProductNumberChange[] = [];
  const seen = new Set<string>();
  for (const li of lineItems) {
    const newNumber = (li.manufacturerProductNumber || "").trim();
    if (!li.inventoryId || !newNumber || seen.has(li.inventoryId)) continue;
    seen.add(li.inventoryId);
    const current = catalogSuggestions?.manufacturerSkus?.[li.inventoryId] ?? null;
    if (current && current.trim().toUpperCase() === newNumber.toUpperCase()) continue;
    changes.push({ inventoryId: li.inventoryId, name: li.inventoryName, currentNumber: current, newNumber });
  }
  return changes;
}

const money = (value: number) => `$${value.toFixed(2)}`;

interface SavePricesToSkuMasterDialogProps {
  open: boolean;
  onClose: () => void;
  poNumber: string;
  changes: PriceChange[];
  productNumberChanges?: ProductNumberChange[];
  /** savedCount = prices + product numbers written */
  onSaved: (savedCount: number) => void;
}

export default function SavePricesToSkuMasterDialog({
  open,
  onClose,
  poNumber,
  changes,
  productNumberChanges = [],
  onSaved,
}: SavePricesToSkuMasterDialogProps): JSX.Element {
  const service = useMemo(() => new SkuMasterService(), []);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedNumbers, setSelectedNumbers] = useState<Set<string>>(new Set());
  // Only used when the PO has no container type: which method the new prices belong to
  const methodUnknown = changes.length > 0 && changes.some(change => change.method == null);
  const [fallbackMethod, setFallbackMethod] = useState<FreightMethod>("40hc");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Product numbers that the SKU master refused (another item holds the number)
  const [numberErrors, setNumberErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(new Set(changes.map(change => change.inventoryId)));
      setSelectedNumbers(new Set(productNumberChanges.map(change => change.inventoryId)));
      setFallbackMethod(changes.find(change => change.currentMethod)?.currentMethod ?? "40hc");
      setErrorMessage(null);
      setNumberErrors({});
    }
  }, [open, changes, productNumberChanges]);

  const handleSave = async () => {
    const rows = changes
      .filter(change => selected.has(change.inventoryId))
      .map(change => ({
        itemId: Number(change.inventoryId),
        method: change.method ?? fallbackMethod,
        unitPrice: change.newPrice,
        currency: "USD",
        quoteDate: moment().format("YYYY-MM-DD"),
        quotationNumber: `PO ${poNumber}`,
        lineDescription: change.name ?? null,
      }));
    const numbers = productNumberChanges.filter(change => selectedNumbers.has(change.inventoryId));
    if (rows.length === 0 && numbers.length === 0) return;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      let saved = 0;
      if (rows.length > 0) {
        const result = await service.confirmQuotes(rows);
        if (!result?.ok) {
          setErrorMessage("Failed to save prices to the SKU master");
          return;
        }
        saved += result.saved;
      }
      // One item at a time so a refused number (held by another item) is
      // reported on its row while the rest still land.
      const failures: Record<string, string> = {};
      for (const change of numbers) {
        try {
          await service.updateItem(Number(change.inventoryId), { manufacturerSku: change.newNumber });
          saved += 1;
        } catch (err: any) {
          failures[change.inventoryId] = err.message;
        }
      }
      setNumberErrors(failures);
      if (Object.keys(failures).length > 0) {
        onSaved(saved);
        setSelected(new Set());
        setSelectedNumbers(new Set(Object.keys(failures)));
        setErrorMessage(`${Object.keys(failures).length} product number${Object.keys(failures).length === 1 ? " was" : "s were"} not saved — see below. Fix the other item on the SKU Master page, or untick.`);
        return;
      }
      onSaved(saved);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const methodLabel = METHOD_LABELS[changes[0]?.method ?? fallbackMethod];
  const totalSelected = selected.size + selectedNumbers.size;

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save to the SKU master?</DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>{errorMessage}</Alert>
        )}
        {productNumberChanges.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {poNumber} has Danican product numbers the SKU master doesn't have for these items.
              Ticked ones are written to the item, so future POs fill them in.
            </Typography>
            <Table size="small" sx={{ mb: changes.length > 0 ? 3 : 0 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedNumbers.size === productNumberChanges.length && productNumberChanges.length > 0}
                      indeterminate={selectedNumbers.size > 0 && selectedNumbers.size < productNumberChanges.length}
                      onChange={(e) => setSelectedNumbers(e.target.checked ? new Set(productNumberChanges.map(c => c.inventoryId)) : new Set())}
                    />
                  </TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>SKU master today</TableCell>
                  <TableCell>This PO</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productNumberChanges.map(change => (
                  <TableRow key={change.inventoryId} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedNumbers.has(change.inventoryId)}
                        onChange={(e) => setSelectedNumbers(prev => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(change.inventoryId); else next.delete(change.inventoryId);
                          return next;
                        })}
                      />
                    </TableCell>
                    <TableCell>
                      {change.name || `Inventory ${change.inventoryId}`}
                      {numberErrors[change.inventoryId] && (
                        <Typography variant="caption" color="error" sx={{ display: "block" }}>{numberErrors[change.inventoryId]}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{change.currentNumber || "— (none)"}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{change.newNumber}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {changes.length > 0 && (
        <>
        {methodUnknown ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {poNumber} has no container type, so it isn't clear which freight method these prices belong to.
              Pick it here (setting the container type on the PO avoids this next time).
            </Alert>
            <FormControl size="small" sx={{ mb: 2, minWidth: 220 }}>
              <InputLabel>Record prices as</InputLabel>
              <Select label="Record prices as" value={fallbackMethod} onChange={(e) => setFallbackMethod(e.target.value as FreightMethod)}>
                {METHOD_PREFERENCE.map(method => <MenuItem key={method} value={method}>{METHOD_LABELS[method]}</MenuItem>)}
              </Select>
            </FormControl>
          </>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {poNumber} has prices that differ from the SKU master's current {methodLabel} price.
          Ticked ones are recorded as the new {methodLabel} price and pre-fill future POs.
          Untick anything that was a one-off.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={selected.size === changes.length && changes.length > 0}
                  indeterminate={selected.size > 0 && selected.size < changes.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(changes.map(c => c.inventoryId)) : new Set())}
                />
              </TableCell>
              <TableCell>Item</TableCell>
              <TableCell align="right">SKU master today</TableCell>
              <TableCell align="right">This PO</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {changes.map(change => (
              <TableRow key={change.inventoryId} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selected.has(change.inventoryId)}
                    onChange={(e) => setSelected(prev => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(change.inventoryId); else next.delete(change.inventoryId);
                      return next;
                    })}
                  />
                </TableCell>
                <TableCell>{change.name || `Inventory ${change.inventoryId}`}</TableCell>
                <TableCell align="right">
                  {change.currentPrice != null ? money(change.currentPrice) : "— (none)"}
                  {change.currentMethod && change.currentMethod !== (change.method ?? fallbackMethod) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{METHOD_LABELS[change.currentMethod]}</Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{money(change.newPrice)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Not now</Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving || totalSelected === 0}>
          {isSaving ? "Saving…" : `Save ${totalSelected} to SKU master`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
